import {
  AttachmentPoint,
  type Connectable,
  ConnectorEnd,
  type Constructor,
} from '../types';
import { fabric } from 'fabric';
import type Connector from './Connector';
import type ShapeBase from '../shapes/ShapeBase';
import { hasLabelBehaviors, isShape } from './guards';
import { labelShapes } from './LabelBehaviors';
import {
  calculateAttachmentPosition,
  STANDOFF_DISTANCE,
} from '../utils/connectorAttachment';

interface ConnectorAttachment {
  connector: Connector;
  whichEnd: ConnectorEnd;
  attachmentPoint: AttachmentPoint;
}

// Behaviours added onto objects that support having connectors attached.

interface ConnectorAttachmentBehaviors extends Connectable {
  connectorAttachments: {
    connector: Connector;
    whichEnd: ConnectorEnd;
    attachmentPoint: AttachmentPoint;
  }[];

  registeredConnectorEvents: boolean;

  initializeConnectorAttachments(): void;

  attachConnector(
    connector: Connector,
    whichEnd: ConnectorEnd,
    attachmentPoint: AttachmentPoint
  ): void;
  addConnector(
    connector: Connector,
    whichEnd: ConnectorEnd,
    attachmentPoint: AttachmentPoint
  ): void;
  detachConnector(connector: Connector): void;
  connectorsForAttachmentPoint(
    attachmentPoint: AttachmentPoint
  ): ConnectorAttachment[];
  positionConnectors(): void;
  constrainConnectorEnd(
    eventData: MouseEvent,
    connector: Connector,
    whichEnd: ConnectorEnd
  ): void;
  positionForConnectorAttachment(
    attachmentPoint: AttachmentPoint,
    connector: Connector,
    whichEnd: ConnectorEnd
  ): fabric.Point | null;
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
function ConnectorAttachmentBehaviors(
  Base: Constructor<fabric.Object & Connectable>
): any {
  return class extends Base implements ConnectorAttachmentBehaviors {
    __hasConnectorAttachmentBehaviors = true;
    connectorAttachments: ConnectorAttachment[];

    registeredConnectorEvents: boolean;
    shape?: ShapeBase;

    initializeConnectorAttachments() {
      // Connectors attached to this object. Each connector attachment describes
      // where the connector is attached:
      //   cl = left, ct = top, cr = right, cb = bottom
      //   cc = whole object
      //   ce = edge. Also specifies where on edge.
      // We don't care which end of the connector we have - the connector keeps
      // track of that.
      // Note that a connector cannot attach both ends to the same object.
      // {connector, whichEnd, attachmentPoint}
      this.connectorAttachments = [];
    }

    setFromRemote(key, value) {
      super.setFromRemote(key, value);

      // TODO: Only call these if necessary.
      this.positionConnectors();
    }

    attachConnector(
      connector: Connector,
      whichEnd: ConnectorEnd,
      attachmentPoint: AttachmentPoint
    ) {
      const resolvedPoint = this._resolvedAttachmentPoint(
        attachmentPoint,
        connector,
        whichEnd
      );
      if (!resolvedPoint) return;

      const attachmentNormal =
        this.connectControlDefinition()[resolvedPoint].normal;

      connector.attachObject(whichEnd, this, attachmentPoint, attachmentNormal);
      this.addConnector(connector, whichEnd, attachmentPoint);
    }

    addConnector(
      connector: Connector,
      whichEnd: ConnectorEnd,
      attachmentPoint: AttachmentPoint
    ) {
      if (this.findAttachment(connector, whichEnd) == null) {
        this.connectorAttachments.push({
          connector,
          whichEnd,
          attachmentPoint,
        });

        // Update connectors when shape changes. As an optimization we only
        // register these events once there is a connector.
        this.registerConnectorEvents();
        if (hasLabelBehaviors(this)) {
          // Dont allow attachment to be a label
          this.clearLabelBehaviors();
        }
      }
    }

    detachConnector(connector: Connector) {
      this.connectorAttachments = this.connectorAttachments.filter(
        attachment => attachment.connector !== connector
      );

      // Allow ShapeText to become a label
      if (
        hasLabelBehaviors(this) &&
        this.connectorAttachments.length === 0 &&
        isShape(this) &&
        labelShapes.includes(this.shapeName)
      ) {
        this.initializeLabelBehaviors();
      }
    }

    connectorsForAttachmentPoint(attachmentPoint: AttachmentPoint) {
      return this.connectorAttachments.filter(
        attachment => attachment.attachmentPoint === attachmentPoint
      );
    }

    synchronize() {
      this.positionConnectors();
    }

    positionConnectors() {
      // For each connector, move the appropriate end to the location where the
      // control would appear.
      for (let i = 0; i < this.connectorAttachments.length; i++) {
        this.connectorAttachments[i].connector.constrainConnector({});
      }
    }

    registerConnectorEvents() {
      if (this.registeredConnectorEvents) return;
      this.registeredConnectorEvents = true;

      this.on('moving', () => this.positionConnectors());
      this.on('scaling', () => this.positionConnectors());
      this.on('rotating', () => this.positionConnectors());
      this.on('resizing', () => this.positionConnectors());
    }

    constrainConnectorEnd(
      eventData: unknown,
      connector: Connector,
      whichEnd: ConnectorEnd
    ) {
      const attachment = this.findAttachment(connector, whichEnd);
      if (attachment) {
        this.constrainAttachment(eventData, attachment);
      }
    }

    // Find the attachment for a connector.
    findAttachment(connector: Connector, whichEnd: ConnectorEnd) {
      return this.connectorAttachments.find(
        attachment =>
          attachment.connector === connector && attachment.whichEnd === whichEnd
      );
    }

    constrainAttachment(
      eventData: unknown | undefined,
      attachment: {
        connector: Connector;
        whichEnd: ConnectorEnd;
        attachmentPoint: AttachmentPoint;
      }
    ) {
      const connector = attachment.connector;
      // Find the position where the connector should be attached to this
      // shape.
      const position = this.positionForConnectorAttachment(
        attachment.attachmentPoint,
        attachment.connector,
        attachment.whichEnd
      );

      if (!position) return;

      // Use the connector end control to move keep the connector in sync.
      const pointControlId =
        attachment.whichEnd === ConnectorEnd.CONNECTOR_START
          ? 'p0'
          : `p${connector.points.length - 1}`;
      const connectorControl = connector.controls[pointControlId];
      if (connectorControl) {
        const transform = { target: connector, constraining: true };
        const oldCorner = connector.__corner;
        connector.__corner = pointControlId;
        connectorControl.actionHandler(
          // @ts-expect-error we're not passing an actual event here, but it works fine
          eventData,
          transform,
          position.x,
          position.y
        );
        connector.__corner = oldCorner;
        connector.setCoords();
      }
    }

    // Find the best attachment point for a connector to use, which is the
    // point it is actually attached to, or the nearest point on the perimeter
    // to the last control point.
    _resolvedAttachmentPoint(
      attachmentPoint: AttachmentPoint,
      connector: Connector,
      whichEnd: ConnectorEnd
    ): string | null {
      if (attachmentPoint === 'cc') {
        // If we are computing the end of a line connecting to the center,
        // then work out the nearest point on the perimeter of the shape for the
        // connector to use instead.

        const otherEndPoint = connector.penultimatePoint(whichEnd);

        // Compare each attachment point to find the closest.
        const bestAttachment = this.attachmentPoints()
          .filter(attachmentPoint => attachmentPoint !== AttachmentPoint.Center)
          .map(attachmentPoint => {
            return {
              attachmentPoint,
              position: calculateAttachmentPosition(this, attachmentPoint, {
                withStandoff: true,
              }),
            };
          })
          .sort(
            (a, b) =>
              Math.hypot(
                (a.position?.x ?? 0) - otherEndPoint.x,
                (a.position?.y ?? 0) - otherEndPoint.y
              ) -
              Math.hypot(
                (b.position?.x ?? 0) - otherEndPoint.x,
                (b.position?.y ?? 0) - otherEndPoint.y
              )
          )[0];

        if (bestAttachment) {
          return bestAttachment.attachmentPoint;
        } else {
          return null;
        }
      } else {
        return attachmentPoint;
      }
    }

    // Compute the position of the end of the connector for an attachment.
    positionForConnectorAttachment(
      attachmentPoint: AttachmentPoint,
      connector: Connector,
      whichEnd: ConnectorEnd
    ): fabric.Point | null {
      const controlDefinitions = this.connectControlDefinition();

      const resolvedAttachmentPoint = this._resolvedAttachmentPoint(
        attachmentPoint,
        connector,
        whichEnd
      );
      if (!resolvedAttachmentPoint) return null;

      // Update this attachment with the normal from the best attachment.
      const existingAttachment = connector.attachments[whichEnd];
      if (existingAttachment) {
        existingAttachment.normal =
          controlDefinitions[resolvedAttachmentPoint].normal;
      }

      const offsets = controlDefinitions[resolvedAttachmentPoint];

      // Maybe the shape changed.
      if (!offsets) return null;

      const strokeWidth =
        this.stroke && this.stroke !== 'transparent' ? this.strokeWidth : 0;
      const standoff = STANDOFF_DISTANCE;

      return fabric.util.transformPoint(
        new fabric.Point(
          offsets.x * (this.width + strokeWidth / this.scaleX) +
            (offsets.normal.x * standoff) / this.scaleX,

          offsets.y * (this.height + strokeWidth / this.scaleY) +
            (offsets.normal.y * standoff) / this.scaleY
        ),
        this.calcTransformMatrix()
      );
    }
  };
}

export default ConnectorAttachmentBehaviors;
